<template>
  <div class="catalog-page page">
    <h1 class="hidden">{{ pageTitle }}</h1>
    <BreadcrumbsComponent v-if="!title" @back.prevent :links="links" />
    <div class="catalog-page__container sides-padding">
      <div class="container">
        <span v-if="title" class="catalog-page__result">
          По запросу «{{ title }}» найдено:
          <span class="catalog-page__result-highlight">
            {{ products.paginatorInfo.total }}
            {{ products.paginatorInfo.total | plural_word("товар", "товара", "товаров") }}
          </span>
        </span>
        <BrandCardComponent :brand="brand" />
        <div ref="productsList" class="catalog-page__body">
          <CatalogFiltersComponent @filter="filter" :loading="filterLoading" />
          <div class="catalog-page__content">
            <div class="catalog-page__mobile-buttons">
              <button @click="showCategoriesMenu" type="button" class="btn btn--md btn--gray">
                Навигация
              </button>
              <button @click="showFilterMenu" type="button" class="btn btn--md btn--main">Фильтр</button>
            </div>
            <div class="sorts">
              <span class="sorts__title">Сортировать по: </span>
              <button
                class="sorts__item"
                type="button"
                v-for="(sort, i) in sortTypes"
                :key="i"
                @click="changeSort(sort)"
                :class="{
                  'sorts__item--active': JSON.stringify(activeSort) === JSON.stringify(sort),
                }"
              >
                {{ sort.title }}
              </button>
            </div>
            <div
              :style="{ opacity: filterLoading || paginateLoading ? 0.7 : 1 }"
              v-if="showProducts"
              class="catalog-page__items"
            >
              <ProductCardComponent :data="item" v-for="(item, i) in products.data" :key="i" />
            </div>
            <span v-else-if="formData">По данному запросу ничего не найдено</span>
            <span v-else>В данной категории товары отсутвуют</span>
            <PaginationComponent
              v-if="showPagination"
              :page="page"
              :total="Math.ceil(products.paginatorInfo.total / first)"
              @change="paginate"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BreadcrumbsComponent from "components/BreadcrumbsComponent.vue";
import CatalogFiltersComponent from "./components/CatalogFiltersComponent.vue";
import ProductCardComponent from "components/product/ProductCardComponent.vue";
import PaginationComponent from "components/Pagination.vue";
import CategoriesMenu from "components/menus/components/CategoriesMenu.vue";
import FilterMenu from "components/menus/components/FilterMenu.vue";
import CATALOG_PAGE from "@/graphql/pages/CatalogPage.graphql";
import CATALOG_PAGE_CATEGORY from "@/graphql/pages/CatalogPageCategory.graphql";
import CATALOG_PAGE_BRAND from "@/graphql/pages/CatalogPageBrand.graphql";
import BrandCardComponent from "./components/BrandCardComponent.vue";

let _query = {};

let sortTypes = [
  {
    ordering: { orderColumn: "created_at", orderBy: "desc" },
    title: "умолчанию",
  },
  {
    ordering: {
      orderColumn: "in_popular",
      orderBy: "desc",
    },
    title: "популярности",
  },
  {
    ordering: {
      orderColumn: "price",
      orderBy: "asc",
    },
    title: "вначале дешевые",
  },
  {
    ordering: {
      orderColumn: "price",
      orderBy: "desc",
    },
    title: "вначале дорогие",
  },
];

// переделать численные параметры
function parseParams(query) {
  Object.keys(query).forEach((key) => {
    try {
      query[key] = JSON.parse(query[key]);
    } catch (e) {
      return null;
    }
  });
  return query;
}

export default {
  name: "CatalogPage",
  async asyncData({ apollo, store, route }) {
    _query = parseParams(route.query);

    if (_query.brands_item_id) {
      _query.brand_id = [_query.brands_item_id];
    }

    let query = CATALOG_PAGE;
    if (route.params.id) {
      query = CATALOG_PAGE_CATEGORY;
    } else if (_query.brands_item_id) {
      query = CATALOG_PAGE_BRAND;
    }
    await apollo.defaultClient
      .query({
        query: query,
        variables: {
          parent: true,
          category_id: route.params.id ? parseInt(route.params.id) : undefined,
          ordering: sortTypes[0].ordering ? [sortTypes[0].ordering] : undefined,
          first: store.state.isMobile ? 10 : 20,
          ..._query,
        },
      })
      .then(({ data }) => {
        store.state.catalog_page.products = data.products_paginate;
        store.state.catalog_page.category = data.category;
        store.state.catalog_page.filter_attributes = data.filter_attributes;
        store.state.catalog_page.brand = data.brands_item;
        store.state.all_brands = data.brands || [];
        store.state.popular_brands = data.brands_paginate.data || [];
        store.state.categories = data.header_categories.data || [];
        store.state.pages = data.pages;
        store.state.marketplace = data.marketplace;
      });
  },
  data() {
    return {
      page: 1,
      first: this.$store.state.isMobile ? 10 : 20,
      query: _query,
      showFilter: false,
      activeSort: sortTypes[0],
      sortTypes: sortTypes,
      filterLoading: false,
      paginateLoading: false,
      formData: null,
      title: undefined,
      params: {},
    };
  },
  beforeRouteLeave(to, from, next) {
    this.$store.state.variables = {};
    next();
  },
  created() {
    this.params = this.parseParams(this.$route.query);
    this.title = this.params.title;
  },
  watch: {
    "$store.state.variables": {
      handler() {
        this.filter();
      },
      deep: true,
    },
  },
  computed: {
    links() {
      let links = [];
      if (this.category) {
        links.push({ title: this.category.title });
        let category = this.category.parent;
        while (category) {
          links.unshift({
            title: category.title,
            route: {
              name: "catalog",
              params: { id: category.id },
            },
          });
          category = category.parent;
        }
      } else if (this.brand && this.brand.title) {
        links = [{ title: "Бренды", route: { name: "brands" } }, { title: this.brand.title }];
      } else {
        links.push({ title: "Каталог товаров" });
      }
      return links;
    },
    category() {
      return this.$store.state.catalog_page.category;
    },
    brand() {
      return this.$store.state.catalog_page.brand;
    },
    products() {
      return this.$store.state.catalog_page.products;
    },
    showProducts() {
      return this.products && this.products.data && this.products.data.length;
    },
    showPagination() {
      return this.showProducts && Math.ceil(this.products.paginatorInfo.total / this.first) > 1;
    },
    pageTitle() {
      if (this.category) {
        return this.category.title;
      }
      if (this.brand) {
        return this.brand.title;
      }
      return "Каталог товаров";
    },
    metaDescription() {
      if (this.category) {
        return this.category.meta_description;
      }
      if (this.brand) {
        return this.brand.meta_description;
      }
      return null;
    },
    metaKeywords() {
      if (this.category) {
        return this.category.meta_keywords;
      }
      if (this.brand) {
        return this.brand.meta_keywords;
      }
      return null;
    },
  },
  methods: {
    async changeSort(sort) {
      this.activeSort = sort;
      this.page = 1;
      this.paginateLoading = true;
      await this.load();
    },
    async paginate(page) {
      if (!this.paginateLoading) {
        this.page = page;
        this.paginateLoading = true;
        await this.load();
        this.scrollUp();
      }
    },
    async filter(form) {
      this.page = 1;
      this.formData = form;
      this.filterLoading = true;
      await this.load();
      this.scrollUp();
    },
    async load() {
      let query = CATALOG_PAGE;
      if (this.category) {
        query = CATALOG_PAGE_CATEGORY;
      } else if (this.brand) {
        query = CATALOG_PAGE_BRAND;
      }
      await this.$apollo.provider.defaultClient
        .query({
          query: query,
          variables: {
            parent: true,
            category_id: this.category ? this.category.id : undefined,
            ordering: this.activeSort.ordering ? [this.activeSort.ordering] : undefined,
            first: this.first,
            page: this.page,
            ...this.params,
            ...this.$store.state.variables,
          },
        })
        .then(({ data }) => {
          this.filterLoading = false;
          this.paginateLoading = false;
          this.$store.state.catalog_page.products = data.products_paginate;
          this.$store.state.catalog_page.category = data.category;
          this.$store.state.all_brands = data.brands || [];
          this.$store.state.popular_brands = data.brands_paginate.data || [];
          this.$store.state.categories = data.header_categories.data || [];
          this.$store.state.pages = data.pages;
          this.$store.state.marketplace = data.marketplace;
        })
        .catch(() => {
          this.filterLoading = false;
          this.paginateLoading = false;
        });
    },
    scrollUp() {
      if (this.$refs.productsList && window.scrollY > this.$refs.productsList.offsetTop) {
        window.scrollTo({ top: this.$refs.productsList.offsetTop });
      }
    },
    showCategoriesMenu() {
      this.$store.state._menus.push({
        component: CategoriesMenu,
        options: {},
      });
    },
    showFilterMenu() {
      this.$store.state._menus.push({
        component: FilterMenu,
        options: {},
      });
    },
    parseParams,
  },
  components: {
    BrandCardComponent,
    PaginationComponent,
    ProductCardComponent,
    CatalogFiltersComponent,
    BreadcrumbsComponent,
  },
  metaInfo() {
    return {
      title: this.pageTitle,
      description: this.metaDescription,
      keywords: this.metaKeywords,
    };
  },
};
</script>

<style lang="stylus">
@import "~@/styles/parts/sorts.styl"
.catalog-page {
  width 100%
  display grid
  grid-gap 30px

  &__mobile {
    +above(861px) {
      display none
    }
  }

  &__filter-btn {
    justify-content center
    margin 0 15px
  }

  &__body {
    display grid
    grid-template-columns 333fr 935fr
    grid-gap 30px
    align-items start
    +below(1200px) {
      grid-template-columns 4fr 8fr
    }
    +below(860px) {
      grid-template-columns 1fr
    }
  }

  &__content {
    position sticky
    top: 20px
    display grid
    grid-gap 50px
    +below(990px) {
      grid-gap 30px
    }
  }

  &__mobile-buttons {
    display grid
    grid-template-columns 1fr 1fr
    grid-gap 30px
    +above(861px) {
      display none
    }
    +below(420px) {
      grid-gap 20px
    }

    .btn {
      text-transform none
    }
  }

  &__items {
    display grid
    grid-template-columns repeat(4, 1fr)
    grid-gap 30px
    transition var(--transition)
    +below(1200px) {
      grid-template-columns repeat(3, 1fr)
    }
    +below(960px) {
      grid-template-columns repeat(2, 1fr)
    }
    +below(860px) {
      grid-template-columns repeat(3, 1fr)
    }
    +below(700px) {
      grid-template-columns repeat(2, 1fr)
    }
    +below(540px) {
      grid-gap 8px
    }
    +below(380px) {
      grid-template-columns 1fr
    }
  }

  &__container {
    display: flex;
    justify-content: center;
  }

  &__result {
    font-weight: 700;
    font-size: 1.375em;
    line-height: 114%;
    color: var(--main-color-75);
    display: flex;
    align-items: center;
    gap: 10px
    flex-wrap wrap
    transition: var(--transition);
    margin-bottom: 24px;
    +below(480px) {
      font-size 1.125em
    }

    &-highlight {
      color var(--dark)
      margin-left 10px
    }
  }

  &__title {
    max-width var(--main_width)
    width 100%
    font-weight: normal;
    font-size: 2.25em;
    line-height: 50px;
    display: flex;
    align-items: center;
    color: var(--body-color);
    margin 0 auto

    &-container {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0 15px;
    }
  }

  &__category {
    background: var(--white);
    border: 1px solid var(--blue-300);
    box-sizing: border-box;
    border-radius: var(--small_radius);
    padding 24px 0
    width 100%
    display flex
    flex-direction column
    justify-content center
    align-items center
    gap 16px

    &:hover {
      border: 1px solid var(--blue-300);
    }

    img {
      width 80px
      height 80px
      object-fit cover
      object-position center
    }

    span {
      font-weight: normal;
      font-size: 0.875em;
      line-height: 20px;
      display: flex;
      align-items: center;
      color: var(--body-color-muted);
    }
  }

  &__slider {
    width: 100%;
    +below(1240px) {
      padding-right 0
    }
  }
}
</style>
